import { Component, h, Prop, Event, EventEmitter, State, Element } from "@stencil/core";

@Component({
  tag: "datagrid-filter-dropdown",
  shadow: false,
})
export class DatagridFilterDropdown {
  @Element() el!: HTMLElement;
  /**
   * The ID of the grid to which this filter belongs.
   * This is used to identify which grid the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @example "grid-1"
   * @type {string}
   * @required
   * @description The ID of the grid to which this filter belongs.
   * This is used to identify which grid the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @default ""
   */
  @Prop() grid_id!: string;
  /**
   * The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @example "eligible_year"
   * @type {string}
   * @required
   * @description The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @default ""
   */
  @Prop() field!: string;
  /**
   * The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @example "Eligible Year"
   * @type {string}
   * @required
   * @description The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @default ""
   */
  @Prop() label!: string;
  /**
   * The options to be displayed in the filter.
   * This is a JSON string that contains an array of objects with label and value properties.
   * @example '[{"label": "2021", "value": "2021"}, {"label": "2022", "value": "2022"}]'
   */
  @Prop() options!: string;
  /**
   * The initial value of the filter.
   * This is used to set the initial state of the filter when it is first rendered.
   */
  @Prop() initialValue?: string;

  @Event() filterChanged: EventEmitter<{
    grid_id: string;
    field: string;
    value: string;
    type: string;
  }>;

  @State() selectedValue: string = this.initialValue || "";

  handleValueChange(value: any) {
    this.selectedValue = value;
    this.emitFilterChange();
    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.selectedValue,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.selectedValue,
    }));
  }

  emitFilterChange() {
    this.filterChanged.emit({
      grid_id: this.grid_id,
      field: this.field,
      value: this.selectedValue,
      type: "datagrid-filter-dropdown",
    });
  }

  get parsedOptions(): { label: string; value: string }[] {
    try {
      const parsed = this.options ? JSON.parse(this.options) : [];
      if (Array.isArray(parsed)) {
        return parsed.map((opt) =>
          typeof opt === "string" ? { label: opt, value: opt } : opt
        );
      }
      return [];
    } catch (e) {
      console.error("🚨 Error parsing options:", this.options, e);
      return [];
    }
  }

  render() {
    // const optionsArray = this.options ? this.options.split(",").map((opt) => opt.trim()) : [];

    return (
      <div class="datagrid-filter-dropdown">
        <signet-select
          label={this.label}
          onSelectedValue={(e: any) => this.handleValueChange(e.detail)}
        >
          {this.parsedOptions.map((option) => (
            <signet-option
              value={option.value}
              label={option.label}
            ></signet-option>
          ))}
        </signet-select>
      </div>
    );
  }
}
